// @ts-nocheck
// Fresh, minimal "Premium Requests" admin page -- built 2026-09-19 to
// replace the dead Premiums/UsersManagement.tsx scaffolding (its data
// source, GET /users/query, has no matching backend route and never
// worked). This page has exactly one job: let an admin review users
// whose premium_request is "sent" and approve or decline them, going
// through the existing safe backend endpoint
// (PUT /admin/users/premium-request -> approveDeclinePremiumRequest),
// which is the same endpoint the "Make user premium"/"Revoke premium"
// buttons on a user's detail page use. No react-table/Context/Provider
// scaffolding -- plain fetch-on-mount + a table, on purpose.
import React, {useCallback, useEffect, useState} from 'react'
import axios from 'axios'
import {BASE_URL} from '../../../api'
import API_LINKS from '../../../constant/api.links'
import {PageLink, PageTitle} from '../../../_metronic/layout/core'

type PremiumRequestUser = {
  _id: string
  firstName?: string
  lastName?: string
  businessName?: string
  email?: string
  phone?: string
  availableRoles?: string[]
  role?: string
  createdAt?: string
}

const premiumRequestsBreadcrumbs: Array<PageLink> = [
  {
    title: 'Premium Requests',
    path: '/apps/premium-requests',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

const DURATIONS = [1, 6, 12]

type ConfirmState = {id: string; action: 'approve' | 'decline'} | null

const displayName = (user: PremiumRequestUser) =>
  user.businessName ||
  [user.firstName, user.lastName].filter(Boolean).join(' ') ||
  'Unnamed user'

const canPickDuration = (user: PremiumRequestUser) =>
  (user.availableRoles || []).some((role) => ['BUSINESS', 'INDIVIDUAL'].includes(role))

const PremiumRequestsList: React.FC = () => {
  const [requests, setRequests] = useState<PremiumRequestUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // Which row is showing its inline confirm UI (and for which action),
  // plus the duration picked for an in-progress approve.
  const [confirmState, setConfirmState] = useState<ConfirmState>(null)
  const [duration, setDuration] = useState<number>(12)
  // Which row currently has a request in flight -- disables that row's
  // buttons so a slow network can't produce a double-submit.
  const [actioningId, setActioningId] = useState<string | null>(null)

  const fetchRequests = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const {data} = await axios.get(`${BASE_URL}${API_LINKS.GET_USERS}`, {
        params: {premium_request: 'sent', limit: 200},
      })
      setRequests(data?.docs || [])
    } catch (err) {
      console.error(err)
      setError('Could not load pending premium requests. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  const submitDecision = async (user: PremiumRequestUser, action: 'approve' | 'decline') => {
    setActioningId(user._id)
    try {
      await axios.put(`${BASE_URL}${API_LINKS.UPDATE_PREMIUM_REQUEST}`, {
        userId: user._id,
        premium_request: action === 'approve' ? 'approved' : 'declined',
        duration: action === 'approve' ? duration : 0,
      })
      // This request is resolved either way -- drop it from the pending
      // list rather than refetching the whole page.
      setRequests((prev) => prev.filter((u) => u._id !== user._id))
    } catch (err) {
      console.error(err)
      alert(
        action === 'approve'
          ? 'Could not approve this request. Please try again.'
          : 'Could not decline this request. Please try again.'
      )
    } finally {
      setActioningId(null)
      setConfirmState(null)
    }
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <h3 className='fw-bolder'>Pending Premium Requests</h3>
        </div>
        <div className='card-toolbar'>
          <button
            className='btn btn-sm btn-light-primary'
            onClick={fetchRequests}
            disabled={loading}
          >
            <i className='bi bi-arrow-clockwise me-1'></i> Refresh
          </button>
        </div>
      </div>
      <div className='card-body pt-0'>
        {error && <div className='alert alert-danger'>{error}</div>}

        {loading ? (
          <div className='d-flex align-items-center py-10 justify-content-center'>
            <span className='spinner-border spinner-border-sm me-3'></span>
            Loading pending requests...
          </div>
        ) : requests.length === 0 ? (
          <div className='text-muted py-10 text-center'>
            No pending premium requests right now.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Role</th>
                  <th>Requested</th>
                  <th className='text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((user) => {
                  const isActioning = actioningId === user._id
                  const isConfirming = confirmState?.id === user._id

                  return (
                    <tr key={user._id}>
                      <td>
                        <a
                          href={`/apps/all-users/users/details-page/overview/${user._id}`}
                          className='text-gray-800 text-hover-primary fw-bolder'
                        >
                          {displayName(user)}
                        </a>
                      </td>
                      <td>
                        <div>{user.email || '--'}</div>
                        <div className='text-muted'>{user.phone || ''}</div>
                      </td>
                      <td>{(user.availableRoles || []).join(', ') || user.role || '--'}</td>
                      <td>
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '--'}
                      </td>
                      <td className='text-end'>
                        {isConfirming && confirmState.action === 'approve' ? (
                          <div className='d-flex align-items-center justify-content-end gap-2'>
                            {canPickDuration(user) && (
                              <select
                                className='form-select form-select-sm w-auto'
                                value={duration}
                                onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                                disabled={isActioning}
                              >
                                {DURATIONS.map((d) => (
                                  <option key={d} value={d}>
                                    {d} month{d > 1 ? 's' : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                            <button
                              className='btn btn-sm btn-success'
                              disabled={isActioning}
                              onClick={() => submitDecision(user, 'approve')}
                            >
                              Confirm approve
                            </button>
                            <button
                              className='btn btn-sm btn-light'
                              disabled={isActioning}
                              onClick={() => setConfirmState(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : isConfirming && confirmState.action === 'decline' ? (
                          <div className='d-flex align-items-center justify-content-end gap-2'>
                            <span className='text-muted fs-8 me-2'>Decline this request?</span>
                            <button
                              className='btn btn-sm btn-danger'
                              disabled={isActioning}
                              onClick={() => submitDecision(user, 'decline')}
                            >
                              Confirm decline
                            </button>
                            <button
                              className='btn btn-sm btn-light'
                              disabled={isActioning}
                              onClick={() => setConfirmState(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className='d-flex align-items-center justify-content-end gap-2'>
                            <button
                              className='btn btn-sm btn-light-success'
                              disabled={isActioning}
                              onClick={() => {
                                setDuration(12)
                                setConfirmState({id: user._id, action: 'approve'})
                              }}
                            >
                              Approve
                            </button>
                            <button
                              className='btn btn-sm btn-light-danger'
                              disabled={isActioning}
                              onClick={() => setConfirmState({id: user._id, action: 'decline'})}
                            >
                              Decline
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

const PremiumRequests: React.FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={premiumRequestsBreadcrumbs}>Premium Requests</PageTitle>
      <PremiumRequestsList />
    </>
  )
}

export default PremiumRequests
